/**
 * WatchTab - Admin tab for managing Watch page content
 * Refactored to use extracted components for maintainability
 */
import { useState, useEffect } from 'react';
import { Plus, X, Video, Layers, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { API, authHeaders } from './utils';

// Import extracted components
import { 
  extractYoutubeId, 
  SortableVideoItem, 
  SortableSectionItem, 
  SortableSectionVideoItem,
  VideoForm,
  SectionForm,
  SettingsPanel
} from './watch';

export const WatchTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [sections, setSections] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Video form state (single featured video)
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoFormData, setVideoFormData] = useState({
    title: '', description: '', youtube_url: '', thumbnail_url: '', duration: ''
  });
  
  // Section form state
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({
    title: '', section_type: 'videos', sport_filter: '', channel_filter: '', show_live_only: false,
    show_upcoming_only: true, max_items: 10, is_active: true
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [availableRecordings, setAvailableRecordings] = useState([]);
  const [showAddRecording, setShowAddRecording] = useState(null);
  const [addingRecording, setAddingRecording] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchVideos(), fetchSections(), fetchSettings(), fetchAvailableRecordings()]);
    setLoading(false);
  };

  const fetchVideos = async () => {
    try {
      const res = await fetch(`${API}/watch/admin/featured-videos`, { headers: authHeaders() });
      if (res.ok) setVideos(await res.json() || []);
    } catch (err) { console.error('Error fetching videos:', err); }
  };

  const fetchSections = async () => {
    try {
      const res = await fetch(`${API}/watch/admin/sections`, { headers: authHeaders() });
      if (res.ok) setSections(await res.json() || []);
    } catch (err) { console.error('Error fetching sections:', err); }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/watch/settings`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSettings(data || {});
      }
    } catch (err) { console.error('Error fetching settings:', err); }
  };

  const fetchAvailableRecordings = async () => {
    try {
      const res = await fetch(`${API}/watch/admin/available-recordings`, { headers: authHeaders() });
      if (res.ok) setAvailableRecordings(await res.json() || []);
    } catch (err) { console.error('Error fetching recordings:', err); }
  };

  // Video handlers
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    if (!videoFormData.title.trim() || !videoFormData.youtube_url.trim()) {
      setError('Title and YouTube URL are required'); return;
    }
    setSaving(true); setError('');
    try {
      const url = editingVideo ? `${API}/watch/admin/featured-videos/${editingVideo.id}` : `${API}/watch/admin/featured-videos`;
      const res = await fetch(url, {
        method: editingVideo ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(videoFormData)
      });
      if (res.ok) { await fetchVideos(); resetVideoForm(); }
      else setError((await res.json()).detail || 'Failed to save video');
    } catch (err) { setError('Failed to save video'); }
    finally { setSaving(false); }
  };

  const handleVideoEdit = (video) => {
    setEditingVideo(video);
    setVideoFormData({
      title: video.title || '', description: video.description || '', youtube_url: video.youtube_url || '',
      thumbnail_url: video.thumbnail_url || '', duration: video.duration || ''
    });
    setShowVideoForm(true);
  };

  const handleVideoDelete = async (videoId) => {
    if (!window.confirm('Delete this featured video?')) return;
    try {
      const res = await fetch(`${API}/watch/admin/featured-videos/${videoId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) await fetchVideos();
    } catch (err) { console.error('Error deleting video:', err); }
  };

  const handleVideoToggle = async (videoId, currentActive) => {
    try {
      await fetch(`${API}/watch/admin/featured-videos/${videoId}`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentActive })
      });
      await fetchVideos();
    } catch (err) { console.error('Error toggling video:', err); }
  };

  const resetVideoForm = () => {
    setShowVideoForm(false); setEditingVideo(null);
    setVideoFormData({ title: '', description: '', youtube_url: '', thumbnail_url: '', duration: '' });
  };

  // Section handlers
  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    if (!sectionFormData.title.trim()) { setError('Section title is required'); return; }
    setSaving(true); setError('');
    try {
      const url = editingSection ? `${API}/watch/admin/sections/${editingSection.id}` : `${API}/watch/admin/sections`;
      const res = await fetch(url, {
        method: editingSection ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionFormData)
      });
      if (res.ok) { await fetchSections(); resetSectionForm(); }
      else setError((await res.json()).detail || 'Failed to save section');
    } catch (err) { setError('Failed to save section'); }
    finally { setSaving(false); }
  };

  const handleSectionEdit = (section) => {
    setEditingSection(section);
    setSectionFormData({
      title: section.title || '', section_type: section.section_type || 'videos',
      sport_filter: section.sport_filter || '', channel_filter: section.channel_filter || '',
      show_live_only: section.show_live_only || false,
      show_upcoming_only: section.show_upcoming_only !== false, max_items: section.max_items || 10,
      is_active: section.is_active !== false
    });
    setShowSectionForm(true);
  };

  const handleSectionDelete = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      const res = await fetch(`${API}/watch/admin/sections/${sectionId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) await fetchSections();
    } catch (err) { console.error('Error deleting section:', err); }
  };

  const handleSectionToggle = async (sectionId, currentActive) => {
    try {
      await fetch(`${API}/watch/admin/sections/${sectionId}`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentActive })
      });
      await fetchSections();
    } catch (err) { console.error('Error toggling section:', err); }
  };

  const handleAddRecordingToSection = async (sectionId, recordingId) => {
    setAddingRecording(true);
    try {
      const res = await fetch(`${API}/watch/admin/sections/${sectionId}/add-recording?recording_id=${recordingId}`, {
        method: 'POST', headers: authHeaders()
      });
      if (res.ok) { await fetchSections(); setShowAddRecording(null); }
      else setError((await res.json()).detail || 'Failed to add recording');
    } catch (err) { setError('Failed to add recording'); }
    finally { setAddingRecording(false); }
  };

  const handleRemoveVideoFromSection = async (sectionId, videoId) => {
    try {
      const res = await fetch(`${API}/watch/admin/sections/${sectionId}/videos/${videoId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) await fetchSections();
      else setError((await res.json()).detail || 'Failed to remove video');
    } catch (err) { setError('Failed to remove video'); }
  };

  const handleAddYoutubeToSection = async (sectionId, url, title) => {
    try {
      const res = await fetch(`${API}/watch/admin/sections/${sectionId}/add-youtube`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, title: title || '' })
      });
      if (res.ok) await fetchSections();
      else setError((await res.json()).detail || 'Failed to add YouTube URL');
    } catch (err) { setError('Failed to add YouTube URL'); }
  };

  const handleRemoveYoutubeFromSection = async (sectionId, itemId) => {
    try {
      const res = await fetch(`${API}/watch/admin/sections/${sectionId}/youtube/${itemId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) await fetchSections();
      else setError((await res.json()).detail || 'Failed to remove YouTube item');
    } catch (err) { setError('Failed to remove YouTube item'); }
  };

  const handleSectionDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      const newOrder = arrayMove(sections, oldIndex, newIndex);
      setSections(newOrder);
      await fetch(`${API}/watch/admin/sections/reorder`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder.map(s => s.id))
      });
    }
  };

  const handleSectionVideoDragEnd = async (event, sectionId) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const section = sections.find(s => s.id === sectionId);
      if (!section || !section.videos) return;
      const oldIndex = section.videos.findIndex(v => v.id === active.id);
      const newIndex = section.videos.findIndex(v => v.id === over.id);
      const newOrder = arrayMove(section.videos, oldIndex, newIndex);
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, videos: newOrder } : s));
      await fetch(`${API}/watch/admin/sections/${sectionId}/reorder-videos`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder.map(v => v.id))
      });
    }
  };

  const resetSectionForm = () => {
    setShowSectionForm(false); setEditingSection(null);
    setSectionFormData({ title: '', section_type: 'videos', sport_filter: '', channel_filter: '', show_live_only: false, show_upcoming_only: true, max_items: 10, is_active: true });
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/watch/admin/settings`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      await fetchSettings();
    } catch (err) { console.error('Error saving settings:', err); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Watch Page</h2>
        <p className="text-zinc-500 text-sm">Manage featured videos and custom sections</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button onClick={() => setActiveSubTab('videos')} className={`px-4 py-2 rounded-lg transition-colors ${activeSubTab === 'videos' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
          <Video className="w-4 h-4 inline mr-2" /> Featured Video
        </button>
        <button onClick={() => setActiveSubTab('sections')} className={`px-4 py-2 rounded-lg transition-colors ${activeSubTab === 'sections' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
          <Layers className="w-4 h-4 inline mr-2" /> Custom Sections ({sections.length})
        </button>
        <button onClick={() => setActiveSubTab('settings')} className={`px-4 py-2 rounded-lg transition-colors ${activeSubTab === 'settings' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
          <Settings className="w-4 h-4 inline mr-2" /> Settings
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Featured Video Tab (single video only) */}
      {activeSubTab === 'videos' && (
        <div className="space-y-4">
          {videos.length === 0 && !showVideoForm && (
            <Button onClick={() => setShowVideoForm(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="add-featured-btn">
              <Plus className="w-4 h-4 mr-2" /> Set Featured Video
            </Button>
          )}
          {showVideoForm && (
            <VideoForm
              videoFormData={videoFormData}
              setVideoFormData={setVideoFormData}
              editingVideo={editingVideo}
              onSubmit={handleVideoSubmit}
              onCancel={resetVideoForm}
              saving={saving}
            />
          )}
          {videos.length === 0 && !showVideoForm ? (
            <div className="text-center py-12 bg-zinc-900/50 rounded-xl border border-white/5">
              <Video className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-500">No featured video set</p>
              <p className="text-zinc-600 text-xs mt-1">Add a video to display as the hero banner on the Watch page</p>
            </div>
          ) : videos.length > 0 && (
            <div className="space-y-2">
              {videos.slice(0, 1).map(video => (
                <SortableVideoItem key={video.id} video={video} onEdit={handleVideoEdit} onDelete={handleVideoDelete} onToggle={handleVideoToggle} />
              ))}
              {videos.length > 1 && (
                <p className="text-zinc-500 text-xs">Only the first video is displayed. Remove extra videos to keep things clean.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Custom Sections Tab */}
      {activeSubTab === 'sections' && (
        <div className="space-y-4">
          {!showSectionForm && (
            <Button onClick={() => setShowSectionForm(true)} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" /> Add Custom Section
            </Button>
          )}
          {showSectionForm && (
            <SectionForm
              sectionFormData={sectionFormData}
              setSectionFormData={setSectionFormData}
              editingSection={editingSection}
              onSubmit={handleSectionSubmit}
              onCancel={resetSectionForm}
              saving={saving}
            />
          )}
          {sections.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/50 rounded-xl border border-white/5">
              <Layers className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-500">No custom sections yet</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {sections.map(section => (
                    <SortableSectionItem
                      key={section.id}
                      section={section}
                      onEdit={handleSectionEdit}
                      onDelete={handleSectionDelete}
                      onToggle={handleSectionToggle}
                      onAddRecording={() => setShowAddRecording(section.id)}
                      showAddRecording={showAddRecording === section.id}
                      availableRecordings={availableRecordings}
                      onSelectRecording={(recId) => handleAddRecordingToSection(section.id, recId)}
                      onCloseAddRecording={() => setShowAddRecording(null)}
                      addingRecording={addingRecording}
                      expandedSection={expandedSection}
                      onToggleExpand={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      onRemoveVideo={(videoId) => handleRemoveVideoFromSection(section.id, videoId)}
                      onVideoDragEnd={(e) => handleSectionVideoDragEnd(e, section.id)}
                      sensors={sensors}
                      onAddYoutube={handleAddYoutubeToSection}
                      onRemoveYoutube={handleRemoveYoutubeFromSection}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeSubTab === 'settings' && (
        <SettingsPanel
          settings={settings}
          setSettings={setSettings}
          onSaveSettings={handleSaveSettings}
          saving={saving}
        />
      )}
    </div>
  );
};

export default WatchTab;
